// src/components/cards/InfoRevenuCard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

type Props = {
  date: string; // format YYYY-MM-DD
  title?: string;
};

type Bucket = {
  total_ht: number;
  total_ttc: number;
  encours: number;
};

type ResponseShape = {
  date: string;
  year: number;
  month: number;
  factures: {
    journalier: Bucket;
    mensuel: Bucket;
    annuel: Bucket;
  };
  reglements: {
    journalier: number;
    mensuel: number;
    annuel: number;
  };
};

type Period = 'journalier' | 'mensuel' | 'annuel';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

export default function InfoRevenuCard({ date, title = 'Revenus & règlements' }: Props) {
  const [data, setData] = useState<ResponseShape | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<Period>('mensuel');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get<ResponseShape>('/info-revenu', { params: { date } });
        if (mounted) setData(res.data);
      } catch (e: any) {
        if (mounted) setError(e?.response?.data?.error ?? 'Erreur de chargement');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [date]);

  const bucket = useMemo(() => {
    if (!data) return null;
    return {
      fact: data.factures[period],
      regl: data.reglements[period],
    };
  }, [data, period]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      {/* header */}
      <div className="mb-6 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            {title}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Date de référence :{' '}
            <span className="text-red-500 dark:text-red-400">
              {date}
            </span>
          </p>
        </div>

        {/* menu (placeholder) */}
        <div className="relative">
          <button
            className="rounded-lg p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white"
            aria-label="Options"
            title="Options"
          >
            <svg className="fill-current" width="22" height="22" viewBox="0 0 24 24">
              <path d="M12 6.75A1.75 1.75 0 1 0 12 3.25a1.75 1.75 0 0 0 0 3.5Zm0 7.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Zm0 7.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z" />
            </svg>
          </button>
        </div>
      </div>

      {/* period switch */}
      <div className="mb-5 inline-flex overflow-hidden rounded-lg bg-gray-100 p-0.5 dark:bg-gray-900">
        {(['journalier', 'mensuel', 'annuel'] as Period[]).map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={[
              'px-3 py-2 text-sm font-medium transition',
              period === p
                ? 'bg-white shadow-sm text-gray-900 dark:bg-gray-800 dark:text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            ].join(' ')}
          >
            {p === 'journalier' ? 'Jour' : p === 'mensuel' ? 'Mois' : 'Année'}
          </button>
        ))}
      </div>

      {/* content */}
      {loading && <div className="text-gray-500">Chargement…</div>}
      {error && <div className="text-red-600">{error}</div>}
      {!loading && !error && bucket && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* HT */}
          <div className="flex items-end justify-between py-3">
            <div>
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Total HT</p>
              <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(bucket.fact.total_ht)}
              </h4>
              <span className="text-xs text-gray-400 dark:text-gray-500">Factures {period}</span>
            </div>
          </div>

          {/* TTC */}
          <div className="flex items-end justify-between py-3">
            <div>
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Total TTC</p>
              <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(bucket.fact.total_ttc)}
              </h4>
              <span className="text-xs text-gray-400 dark:text-gray-500">Factures {period}</span>
            </div>
          </div>

          {/* Encours */}
          <div className="flex items-end justify-between py-3">
            <div>
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Encours clients</p>
              <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(bucket.fact.encours)}
              </h4>
              <span className="text-xs text-gray-400 dark:text-gray-500">Solde {period}</span>
            </div>
          </div>

          {/* Règlements */}
          <div className="flex items-end justify-between py-3">
            <div>
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Règlements</p>
              <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(bucket.regl)}
              </h4>
              <span className="text-xs text-gray-400 dark:text-gray-500">Encaissements {period}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
